import { useI18nContext } from '@/i18n/i18n-react';
import { Loader2 } from 'lucide-react';
import { DownloadsChart } from '@/components/downloads-chart';
import { CumulativeDownloadsChart } from '@/components/cumulative-downloads-chart';
import { SessionsChart } from '@/components/sessions-chart';
import { InstalledVersionsCard } from '@/components/metrics/installed-versions-card';
import { NoDataPlaceholder } from '@/components/metrics/no-data-placeholder';
import { Card, CardContent } from '@/components/ui/card';
import { ChartCardHeader } from '@/components/metrics/chart-card-header';
import { SectionHeader } from '@/components/metrics/section-header';
import { UsersMetricsGrid } from '@/components/metrics/users-metrics-grid';
import { formatNumber } from '@/components/metrics/formatters';
import {
  ANALYTICS_SYNC_ID,
  type UsersSectionProps,
} from '@/components/metrics/metric-sections.types';

export function UsersSection({
  userMetrics,
  isLoadingUserMetrics,
  isLoadingDataSufficiency,
  dateFilterLabel,
  activeUsersSufficiency,
  granularity,
  chartRange,
  appDownloadsChart,
  appDownloadsCumulativeChart,
  isLoadingAppDownloadsChart,
  isLoadingAppDownloadsCumulativeChart,
  downloadsDelta,
  comparison,
  appSessionsChart,
  isLoadingAppSessionsChart = false,
  installedVersions,
}: UsersSectionProps) {
  const { LL, locale } = useI18nContext();
  const analyticsSyncId = ANALYTICS_SYNC_ID;

  return (
    <div>
      <SectionHeader
        title={LL.metricsPage.sections.users.title()}
        description={LL.metricsPage.sections.users.description()}
      />
      <UsersMetricsGrid
        userMetrics={userMetrics}
        isLoadingUserMetrics={isLoadingUserMetrics}
        isLoadingDataSufficiency={isLoadingDataSufficiency}
        dateFilterLabel={dateFilterLabel}
        activeUsersSufficiency={activeUsersSufficiency}
        downloadsDelta={downloadsDelta}
        comparison={comparison}
      />

      <div className="mt-8 grid grid-cols-1 gap-4 min-[1200px]:grid-cols-2 min-[1800px]:grid-cols-3">
        <Card>
          <ChartCardHeader
            title={LL.metricsPage.charts.downloads.title()}
            total={formatNumber(userMetrics?.downloads, locale)}
            helpKey="downloadsChart"
          />
          <CardContent>
            <div className="flex flex-col gap-4 w-full h-[200px]">
              {isLoadingAppDownloadsChart ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <DownloadsChart
                  range={chartRange}
                  granularity={granularity}
                  points={appDownloadsChart?.points ?? []}
                  syncId={analyticsSyncId}
                  syncMethod="value"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* ANLT-06: sessions, with the same data warning as Usuários ativos. */}
        <Card>
          <ChartCardHeader
            title={LL.metricsPage.usersTab.sessions()}
            total={
              appSessionsChart
                ? formatNumber(
                    appSessionsChart.points.reduce(
                      (sum, point) => sum + point.sessionsCount,
                      0
                    ),
                    locale
                  )
                : undefined
            }
            helpKey="sessionsChart"
            dataSufficiency={activeUsersSufficiency}
            isLoadingDataSufficiency={isLoadingDataSufficiency}
          />
          <CardContent>
            <div className="flex flex-col gap-4 w-full h-[200px]">
              {isLoadingAppSessionsChart || isLoadingDataSufficiency ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : activeUsersSufficiency &&
                !activeUsersSufficiency.canDisplay ? (
                <NoDataPlaceholder fullHeight />
              ) : (
                <SessionsChart
                  range={chartRange}
                  granularity={granularity}
                  data={appSessionsChart ?? { points: [], campaigns: {} }}
                  syncId={analyticsSyncId}
                  syncMethod="value"
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <ChartCardHeader
            title={LL.metricsPage.charts.downloadsCumulative.title()}
            total={formatNumber(userMetrics?.totalDownloads, locale)}
            helpKey="downloadsCumulativeChart"
          />
          <CardContent>
            <div className="flex flex-col gap-4 w-full h-[200px]">
              {isLoadingAppDownloadsCumulativeChart ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <CumulativeDownloadsChart
                  range={chartRange}
                  granularity={granularity}
                  points={appDownloadsCumulativeChart?.points ?? []}
                  syncId={analyticsSyncId}
                  syncMethod="value"
                />
              )}
            </div>
          </CardContent>
        </Card>

        <InstalledVersionsCard data={installedVersions} />

        {/* <Card>
          <ChartCardHeader
            title={LL.metricsPage.activeUsers.chartTitle()}
            total={formatNumber(userMetrics?.activeUsers, locale)}
            helpKey="activeUsersChart"
            dataSufficiency={activeUsersSufficiency}
          />
          <CardContent>
            <div className="flex flex-col gap-4 w-full h-[200px]">
              {isLoadingActiveUsersChart ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : isLoadingDataSufficiency ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : activeUsersSufficiency &&
                !activeUsersSufficiency.canDisplay ? (
                <NoDataPlaceholder fullHeight />
              ) : (
                <ActiveUsersChart
                  granularity={granularity}
                  points={activeUsersChart?.points ?? []}
                  syncId={analyticsSyncId}
                  syncMethod="value"
                />
              )}
            </div>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
}
